import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useProducts } from "@/context/ProductsContext";
import { imgSrc } from "@/lib/api";
import { MaskLines, FadeUp, Eyebrow } from "@/components/site/Reveal";

const NewTile = ({ p, span, big, delay }) => (
  <FadeUp delay={delay} className={span}>
    <Link to={`/products/${p.slug}`} data-testid={`new-range-${p.slug}`}
      className="group relative flex h-full min-h-[380px] flex-col justify-between overflow-hidden border border-white/10 bg-[#141414] p-6 transition-colors duration-500 hover:border-[#FF5A00] md:p-8">
      <div className="flex items-start justify-between">
        <span className="bg-[#FFC400] px-2 py-1 font-mono text-[11px] font-medium uppercase tracking-wider text-[#0B0B0B]">New</span>
        <ArrowUpRight className="h-6 w-6 text-white transition-transform duration-500 group-hover:rotate-45 group-hover:text-[#FF5A00]" />
      </div>
      <div className={`relative mx-auto my-6 w-full overflow-hidden bg-white ${big ? "aspect-[4/3] max-w-[640px] md:flex-1 [clip-path:polygon(0_0,100%_0,100%_86%,86%_100%,0_100%)]" : "aspect-[16/10] max-w-[420px] [clip-path:polygon(0_0,100%_0,100%_80%,88%_100%,0_100%)]"}`}>
        <img src={imgSrc(p.image)} alt={p.name} loading="lazy" className="h-full w-full object-contain p-4 transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{p.category} · MOQ 100 kg</p>
        <h3 className={`font-wide mt-2 font-black uppercase leading-[0.95] tracking-tight text-white ${big ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"}`}>{p.name}</h3>
        <p className="mt-3 max-w-md text-sm text-zinc-300">{p.tagline}</p>
      </div>
    </Link>
  </FadeUp>
);

export const NewRange = () => {
  const { products } = useProducts();
  const items = products.filter((p) => p.isNew).slice(0, 3);
  if (!items.length) return null;
  return (
    <section data-testid="new-range-section" className="grain relative bg-[#0B0B0B] text-white">
      <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-8 md:grid-cols-12">
          <div className="md:col-span-7">
            <Eyebrow light>The new range — {String(items.length).padStart(2, "0")} inks</Eyebrow>
            <h2 className="font-wide mt-6 text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-5xl lg:text-7xl">
              <MaskLines onView lines={["Built for", <span key="s" className="text-[#FFC400]">heavy-duty</span>, "sacks."]} />
            </h2>
          </div>
          <FadeUp className="self-end md:col-span-4 md:col-start-9">
            <p className="text-base leading-relaxed text-zinc-300">
              Three new ink systems for the bags that work hardest — food-carrying jumbo bags, hot-filled cement sacks and fertilizer bags that get stacked, moved and handled.
            </p>
          </FadeUp>
        </div>
        <div className={`mt-16 grid gap-4 md:grid-cols-12 ${items.length === 3 ? "md:grid-rows-2" : ""}`}>
          {items.map((p, i) => {
            const three = items.length === 3;
            const span = three ? (i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5") : items.length === 2 ? "md:col-span-6" : "md:col-span-12";
            return <NewTile key={p.slug} p={p} span={span} big={three && i === 0} delay={i * 0.1} />;
          })}
        </div>
      </div>
    </section>
  );
};
